import { t } from "@lingui/core/macro";
import { SlideshowItem } from "components/Slideshow/Slideshow";
import { isRealPort } from "src/utils/cruise";
import {
  CruiseItineraryFragment,
  CruiseItineraryPortSlideshowFragment,
  CruiseQuery
} from "types/graphql-types";
import type { WindowLocation } from "@reach/router";
import { isPresent } from "src/utils/checks";
import { FareCode } from "@silversea-ssc/web-fares";
import { StickyMenuSectionRef } from "components/StickyMenu/StickyMenu";

function slideshowForItinerary(
  itinerary: (
    | (CruiseItineraryFragment & CruiseItineraryPortSlideshowFragment)
    | undefined
  )[]
): SlideshowItem[] {
  let processedPorts = new Array<string>();

  return itinerary.reduce<SlideshowItem[]>((images, item) => {
    if (item && item.port?.slideshow) {
      const portCode = item.port?.data?.code;

      if (
        portCode &&
        isRealPort(portCode) &&
        !processedPorts.includes(portCode)
      ) {
        processedPorts = [...processedPorts, portCode];
        images = [
          ...images,
          ...(item.port?.slideshow?.filter(isPresent) || [])
        ];
      }
    }
    return images;
  }, []);
}

// TODO: try to move to graphql resolver
export function collectCruiseSlideshow(
  cruise: CruiseQuery["cruise"]
): SlideshowItem[] {
  if (!cruise || !cruise.data) return [];

  const {
    data: { itinerary }
  } = cruise;

  return isPresent(itinerary) ? slideshowForItinerary(itinerary) : [];
}

export function buildPdfPath(
  location: WindowLocation,
  country?: string
): string {
  //const queryParams = parse(location.search.slice(1));

  const queryParams = new URLSearchParams(location.search);
  const ccptID = queryParams.get("ccpt");

  const params = {
    ...(country ? { country } : {}),
    url: `${location.protocol}//${location.hostname}${location.pathname}`,
    ...(ccptID ? { ccpt: ccptID } : {})
  };

  const newParams = new URLSearchParams(params);
  const queryString = newParams.toString();

  return `${process.env.GATSBY_CRUISE_PDF_URL}?${queryString}`;
}

export const showFareSpecificOfferDisclaimer = (
  cruiseFares: FareCode[],
  offerFares: readonly FareCode[]
): FareCode[] => {
  const cruiseFaresValidForOffer = cruiseFares.filter((x) =>
    offerFares.includes(x)
  );
  return isValidForAllCruiseFares(cruiseFaresValidForOffer, cruiseFares)
    ? []
    : cruiseFaresValidForOffer;
};

const isValidForAllCruiseFares = (
  cruiseFaresValidForOffer: FareCode[],
  cruiseFares: FareCode[]
) => cruiseFaresValidForOffer.length === cruiseFares.length;

export const buildMenuSections = ({
  hasExcursions,
  withExpeditionTeam,
  withEvents,
  withShip,
  withSilverseaHotel,
  withPrePost,
  withStories
}): StickyMenuSectionRef[] => {
  return [
    {
      title: hasExcursions ? t`Itinerary & Excursions` : t`Itinerary`,
      id: "itinerary",
      trackingName: "itinerary"
    },
    { title: t`Fares & Offers`, id: "offers", trackingName: "offers" },
    withExpeditionTeam
      ? {
          title: t`Expedition Team`,
          id: "expeditionTeam",
          trackingName: "expedition-team"
        }
      : undefined,
    withEvents
      ? {
          title: t`cruise-events-section-title`,
          id: "events",
          trackingName: "events"
        }
      : undefined,
    { title: t`Suites & Fares`, id: "suites", trackingName: "suites" },
    withShip
      ? {
          title: t`Ship`,
          id: "ship",
          trackingName: "ship"
        }
      : undefined,

    withSilverseaHotel
      ? {
          title: t`Hotel`,
          id: "silversea-hotel",
          trackingName: "silversea-hotel"
        }
      : undefined,

    withPrePost
      ? {
          title: t`Pre-post hotels`,
          id: "prePost",
          trackingName: "pre-post-hotels"
        }
      : undefined,
    withStories
      ? {
          title: t`Stories`,
          id: "stories",
          trackingName: "blog"
        }
      : undefined
  ].filter(isPresent);
};
